/**
 * @file FooterBottomGroup.tsx
 * @description Группа «Нижняя полоса» вкладки «Подвал» (phase19.16, приёмка
 * Р2а; SB114 — копирайт «© годы правообладатель»; SB19 — сведения о
 * продавце под замком; SB118 — вид полосы; SB122 §4.1 — группа-карточка):
 * копирайт и сразу под ним «Где копирайт», замок «Сведения о продавце»,
 * подгруппы «Вид полосы», «Ссылки», «Связь с продавцом».
 *
 * Копирайт считается общим `formatFooterCopyright` (через
 * `copyrightPreview`) с наименованием продавца из реквизитов — так же, как
 * его покажет витрина: «© 2024–2026 ИП …». Сводка свёрнутой группы — место
 * копирайта и число ссылок на сайте; нет способа связи или сведений о
 * продавце — значок ошибки (сайт с таким подвалом не опубликуется).
 *
 * Партия «Юридические документы» (SB142): свои строки «Ссылок» (`footerDocs`)
 * входят в число ссылок сводки, если видны на сайте.
 */

import {
  FOOTER_PLATFORM_LINK_LABELS,
  FOOTER_PLATFORM_LINKS,
  orderFooterPlatformLinks,
  type FooterPlatformLink,
  type SiteFooterDocs,
  type SiteTheme,
} from "@chaiberi/shared";

import { SettingsGroup } from "../../ui/settings-groups";
import { useCurrentContext } from "../../../auth/hooks";
import { SellerLegalView } from "../SellerLegalBlock";
import { FooterCopyrightPlace, FooterLegalSettings } from "./FooterLegalSettings";
import { FooterShowRow } from "./FooterShowRow";
import type { FooterLinksPatch } from "./FooterLinksGroup";
import type { LegalTarget } from "../../../lib/api-legal-targets";
import type { SellerInfoState } from "../../../hooks/site-view/useSellerInfo";
import { copyrightPreview } from "../../../lib/site-view/brand-text";
import { footerDocRowView, footerLinkWhere, type FooterLegalDocsState } from "../../../lib/site-view/footer-link-where";
import { plural } from "../../../lib/site-view/footer-columns";
import type { SiteBrandDraft } from "../../../lib/site-view/types";

type Footer = SiteTheme["footer"];

/** Место копирайта в сводке. */
const PLACE_SUMMARY: Readonly<Record<Footer["legal"]["copyrightPlace"], string>> = {
  top: "копирайт сверху",
  bottom: "копирайт снизу",
  about: "копирайт в колонке",
};

interface Props {
  footer: Footer;
  brand: SiteBrandDraft;
  seller: SellerInfoState;
  hiddenLinks: ReadonlySet<FooterPlatformLink>;
  /** Состояние юридических документов — для «где задаётся» (SB121). */
  legalDocs: FooterLegalDocsState;
  /** «Настройки сайта → Подвал» (шаблон копирайта). */
  settingsHref: string;
  onChange: (patch: Partial<Footer>) => void;
  /** Свои строки «Ссылок» (SB142). */
  footerDocs: SiteFooterDocs;
  /** Правка «Ссылок»: `footer.legal` и `footerDocs` одной правкой. */
  onLinks: (patch: FooterLinksPatch) => void;
  legalTargets: LegalTarget[] | null;
  onLegalReload?: () => void;
}

export function FooterBottomGroup({ footer, brand, seller, hiddenLinks, legalDocs, settingsHref, onChange, footerDocs, onLinks, legalTargets, onLegalReload }: Props) {
  const slug = useCurrentContext()?.shop?.slug ?? "";
  const legalDocsHref = `/shop/${slug}/settings/legal-documents`;
  // SB121: где задаётся каждая ссылка платформы.
  const where = footerLinkWhere({
    docs: legalDocs,
    deliveryPage: brand.deliveryTermsPage,
    legalDocsHref,
    siteFooterSettingsHref: settingsHref,
  });
  const legalName = seller.info?.profile?.legalName?.trim() || null;
  const copyright = copyrightPreview(brand.copyrightText, brand.siteName, new Date().getFullYear(), legalName);
  // Фактический состав полосы: «Условия доставки и оплаты» — только с опубликованной страницей; дубли меню — помечены.
  const present = new Set(FOOTER_PLATFORM_LINKS.filter((k) => k !== "delivery" || brand.deliveryTermsPage?.isPublished === true));
  const links = orderFooterPlatformLinks(footer.legal.linksOrder, present).map((key) => ({
    key,
    label: FOOTER_PLATFORM_LINK_LABELS[key],
    note: (footer.legal.hiddenLinks as readonly string[]).includes(key) ? "выключена" : hiddenLinks.has(key) ? "скрыта: есть в меню" : null,
  }));
  const ownShown = footerDocs.items.filter((item) => footerDocRowView(item, legalTargets, legalDocsHref).status === "ok").length;
  const shownLinks = links.filter((l) => l.note === null).length + ownShown;

  // Ошибки, с которыми сайт не опубликуется: нет способа связи или сведений о продавце.
  const noContact = !footer.legal.showPhone && !footer.legal.showEmail;
  const noSeller = seller.info !== null && (seller.info.noDefault || seller.info.missing.length > 0);
  const summary = noContact
    ? "нет связи с продавцом"
    : noSeller
      ? "нет сведений о продавце"
      : `${footer.show.copyright ? PLACE_SUMMARY[footer.legal.copyrightPlace] : "без копирайта"} · ${shownLinks} ${plural(shownLinks, "ссылка", "ссылки", "ссылок")}`;

  return (
    <SettingsGroup
      id="bottom"
      title="Нижняя полоса"
      tocLabel="Полоса"
      hint="Копирайт, сведения о продавце и вид полосы."
      summary={summary}
      status={noContact || noSeller ? "error" : undefined}
    >
      <div className="flex flex-col gap-2">
        <FooterShowRow
          label={`Копирайт «${copyright}»`}
          value="есть"
          checked={footer.show.copyright}
          onChange={(on) => onChange({ show: { ...footer.show, copyright: on } })}
          edit={settingsHref}
        />
        <FooterCopyrightPlace value={footer.legal} onChange={(legal) => onChange({ legal })} />
      </div>
      <SellerLegalView shopEmail={brand.shopContactEmail} seller={seller} links={links} />
      <FooterLegalSettings
        value={footer.legal}
        hiddenLinks={hiddenLinks}
        where={where}
        onChange={(legal) => onChange({ legal })}
        docs={footerDocs}
        onLinks={onLinks}
        legalTargets={legalTargets}
        onLegalReload={onLegalReload}
        legalDocsHref={legalDocsHref}
      />
    </SettingsGroup>
  );
}
